import React from "react";
import { Link } from "react-router-dom";
import { Home, Mail, Phone } from "lucide-react";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const stateName = import.meta.env.VITE_STATE_NAME || "";

  const emailDomain = import.meta.env.VITE_EMAIL_DOMAIN || ``;

  return (
    <footer className="bg-primary text-white">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Home size={24} />
              <h3 className="text-xl font-heading font-semibold">
                {stateName} Mortgage Tools
              </h3>
            </div>
            <p className="text-gray-300 mb-4">
              Helping {stateName} homeowners make informed mortgage decisions
              with simple, anonymous tools.
            </p>
            {/* State Switcher Prompt */}
            {stateName.toLowerCase() === "california" && (
              <div className="mb-4">
                <a
                  href="https://utahmortgagetools.com"
                  className="inline-block text-sm text-white hover:underline transition-colors"
                  rel="noopener noreferrer"
                >
                  Not in California?{" "}
                  <span className="underline font-semibold">
                    Switch to Utah
                  </span>
                </a>
              </div>
            )}
            {stateName.toLowerCase() === "utah" && (
              <div className="mb-4">
                <a
                  href="https://californiamortgagetools.com"
                  className="inline-block text-sm text-white hover:underline transition-colors"
                  rel="noopener noreferrer"
                >
                  Not in Utah?{" "}
                  <span className="underline font-semibold">
                    Switch to California
                  </span>
                </a>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {/* <li>
                <Link
                  to="/"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Home
                </Link>
              </li> */}
              <li>
                <Link
                  to="/is-my-quote-good"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Is My Quote Good?
                </Link>
              </li>

              <li>
                <Link
                  to="/check-my-rate"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Check Rates Now
                </Link>
              </li>
              <li>
                <Link
                  to="/should-i-refinance"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Should I Refinance?
                </Link>
              </li>
              {/* <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  About Us
                </a>
              </li> */}
              {/* <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Terms of Service
                </a>
              </li> */}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">
              Contact Us
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail size={20} className="shrink-0 mt-1" />
                <a
                  href={`mailto:hey@${emailDomain}`}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {`hey@${emailDomain}`}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={20} className="shrink-0 mt-1" />
                <a
                  href="tel:+18014774216"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  (801) 477-4216 (text or call)
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-700">
          <p className="text-center text-gray-400 mb-6">
            &copy; {currentYear} {stateName} Mortgage Tools. All rights
            reserved.
          </p>

          {/* Legal Disclaimers */}
          <div className="text-xs text-gray-400 space-y-4 max-w-4xl mx-auto">
            <div>
              <p className="mb-2">
                <strong>About Us: </strong>
                {stateName} Mortgage Tools is a free service with the goal of
                saving people money on their mortgages. Quotes and analysis are
                provided by a team of mortgage brokers at Keystone Financial
                Services, LLC NMLS #150818. {stateName} Mortgage Tools is not a
                lender and does not make mortgage loans. The brokers at Keystone
                Financial Services, LLC are not compensated by this website.
              </p>
            </div>

            <div>
              <p className="mb-2">
                <strong>Disclaimer:</strong> {stateName} Mortgage Tools strives
                to keep its information accurate and up to date. All financial
                products and services are presented without warranty. The
                information provided is for educational and informational
                purposes only and should not be construed as financial advice.
                We recommend consulting with qualified financial professionals
                before making any mortgage decisions.
              </p>
            </div>

            <div>
              <p className="mb-2">
                <strong>Rate Quotes:</strong> The quotes that you see on this
                site are intended to be the best estimates of what you can
                expect to receive based on the limited information that we
                collect. Loan Estimates will change based on monthly income,
                debts, credit scores, current rates, and other factors. Rate
                quotes and pre-qualified offers are not binding and are subject
                to change. Actual rates and terms may vary. All rates are
                estimates and should be verified with your chosen lender.
              </p>
            </div>

            <div>
              <p className="mb-2">
                <strong>Not Financial Advice:</strong> The information provided
                on this website is for educational and informational purposes
                only and should not be construed as financial advice. We
                recommend consulting with qualified financial professionals
                before making any mortgage decisions.
              </p>
            </div>

            <div>
              <p className="mb-2">
                <strong>Third-Party Services:</strong> {stateName} Mortgage
                Tools does not share your data with any third party services.{" "}
                {stateName} Mortgage Tools does not receive compensation from
                lenders and other financial institutions for referrals.
              </p>
            </div>

            <div>
              <p className="mb-2">
                <strong>Privacy & Security:</strong> Your personal information
                is protected and will not be shared without your consent. We do
                not share data with any third parties without your permission.
                We use industry-standard security measures to protect your data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
